import React, { memo, useCallback } from "react";

import { useAlert } from "@hl/base-components/lib/alert/useAlert";
import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import {
  DESTRUCTIVE_COLOR,
  SECONDARY_COLOR,
} from "@hl/base-components/lib/theme/button";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { logError } from "@hl/shared-features/lib/services/logger";
import {
  Box,
  Button,
  Group,
  Modal,
  Stack,
  TextInput,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";

import { BlockchainEntity } from "components/BlockchainEntityTitle/types";
import { useDeleteDraftMutation } from "~/features/collections/sections/ContractCollectionDashboard/menu/menu.graphql.generated";
import { GetMergedCollectionByUserPaginatedDocument } from "~/features/tokens/queries/collections.graphql.generated";

interface DeleteDraftFormValues {
  confirmationText: string;
}

const DraftDeleteModal = ({
  entity,
  isOpened,
  onClose,
}: {
  entity: BlockchainEntity;
  isOpened: boolean;
  onClose: () => void;
}) => {
  const [deleteDraft, { loading }] = useDeleteDraftMutation();
  const alert = useAlert();

  const form = useForm<DeleteDraftFormValues>({
    initialValues: {
      confirmationText: "",
    },
    validate: (values) => ({
      confirmationText:
        values.confirmationText !== "DELETE"
          ? 'Type "DELETE" to confirm collection deletion'
          : null,
    }),
  });

  const handleOnSubmit = useCallback(
    form.onSubmit(async () => {
      try {
        const res = await deleteDraft({
          variables: {
            draftId: entity.id,
          },
          refetchQueries: [GetMergedCollectionByUserPaginatedDocument],
        });
        if (res.data && res.data?.deleteDraft.deletedCount <= 0) {
          throw new Error("No deleted drafts");
        }
        onClose();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        alert.error("Error deleting draft");
        await logError({ error, message: "delete draft error" });
      }
    }),
    [deleteDraft, form, entity.id, onClose, alert]
  );

  const handleClose = useCallback(() => {
    form.reset();
    onClose();
  }, [form, onClose]);

  return (
    <Modal
      opened={isOpened}
      onClose={handleClose}
      size="md"
      title="Delete collection"
      withCloseButton={false}
    >
      <form onSubmit={handleOnSubmit}>
        <Stack spacing={24}>
          <Box>
            <Text component="span" size="sm">
              This action will remove this collection from Highlight; if you
              want to hide this project, we recommend archiving it instead.{" "}
            </Text>
            <Text component="span" size="sm" fw={WEIGHT_BOLD}>
              This action is not reversible.
            </Text>
          </Box>
          <TextInput
            maxLength={100}
            label="Type “DELETE” to confirm"
            {...form.getInputProps("confirmationText")}
            onBlur={() => form.validateField("confirmationText")}
          />
        </Stack>
        <ModalSection>
          <Group spacing={16} grow>
            <Button size="lg" color={SECONDARY_COLOR} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              size="lg"
              loading={loading}
              color={DESTRUCTIVE_COLOR}
              type="submit"
              disabled={!form.isValid()}
            >
              Delete
            </Button>
          </Group>
        </ModalSection>
      </form>
    </Modal>
  );
};

export default memo(DraftDeleteModal);
